import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  MIN_COLUMN_WIDTH,
  STRUCTURAL_COLUMN_IDS,
  type VisibilityState,
} from "@salestrak/ui";

import { GetSettingDocument, SetSettingDocument, type SettingValue } from "#app/lib/data";
import { useAppData } from "#app/lib/data";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import type { Omittable } from "@salestrak/ui";

/**
 * useTableViewSettings — per-user, per-table column customization (order /
 * hidden / widths) for DataTable render sites.
 *
 * Persists to the generic user-scoped `setting` key/value bag under
 * `table-view:<tableId>`. That row is user-scoped with workspaceId NULL
 * (settings.resolver.ts), so customization follows the user across
 * workspaces — which is why the localStorage mirror key carries no
 * workspaceId either, unlike use-nav-preferences.
 *
 * Three layers (template: use-nav-preferences):
 *  - Server (`GetSetting`, keyed `table-view:<tableId>`): source of truth once known.
 *  - localStorage mirror: seeds first paint synchronously; re-warmed when
 *    the server disagrees and no session edit is pending.
 *  - Session state: set on every edit for instant UI, wins until reload.
 *
 * Writes debounce ~600ms so a burst of visibility toggles lands as a single
 * `setSetting` (resize already commits once per gesture, at pointer-up).
 * Reset stores `{}` — the mutation's `JSON!` arg is non-null, and an empty
 * object parses back to "no customization".
 */

export type TableViewState = Omittable<{
  /** Consumer column ids in display order (structural ids excluded). */
  order?: string[];
  /** Hidden column ids. */
  hidden?: string[];
  /** Default-hidden column ids the user explicitly unhid. Only meaningful
   *  for ids in the table's `defaultHiddenColumnIds` — the plain hidden
   *  list can't express "shown" because absence there means "default". */
  shown?: string[];
  /** User width overrides in px by column id. */
  widths?: Record<string, number>;
}>;

const PERSIST_DEBOUNCE_MS = 600;
const EMPTY_VISIBILITY: VisibilityState = {};
const EMPTY_SIZING: Record<string, number> = {};

/** Validate a loose JSON payload (server or mirror) into a TableViewState. */
export function parseTableViewState(value: unknown): TableViewState | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const parsed: TableViewState = {};
  // Structural columns (select / __actions__) are never part of a consumer
  // order, never user-hideable, and never carry stored widths; scrub them
  // from every field of a stale persisted payload — the same invariant
  // enforced in one place, not per-field ad hoc.
  if (
    Array.isArray(raw.order) &&
    raw.order.every((id): id is string => typeof id === "string")
  ) {
    const order = raw.order.filter((id) => !STRUCTURAL_COLUMN_IDS.has(id));
    if (order.length > 0) parsed.order = order;
  }
  if (
    Array.isArray(raw.hidden) &&
    raw.hidden.every((id): id is string => typeof id === "string")
  ) {
    const hidden = raw.hidden.filter((id) => !STRUCTURAL_COLUMN_IDS.has(id));
    if (hidden.length > 0) parsed.hidden = hidden;
  }
  if (
    Array.isArray(raw.shown) &&
    raw.shown.every((id): id is string => typeof id === "string")
  ) {
    const shown = raw.shown.filter((id) => !STRUCTURAL_COLUMN_IDS.has(id));
    if (shown.length > 0) parsed.shown = shown;
  }
  if (
    raw.widths &&
    typeof raw.widths === "object" &&
    !Array.isArray(raw.widths)
  ) {
    const widths: Record<string, number> = {};
    for (const [id, width] of Object.entries(raw.widths)) {
      if (STRUCTURAL_COLUMN_IDS.has(id)) continue;
      if (typeof width === "number" && Number.isFinite(width)) {
        widths[id] = Math.max(MIN_COLUMN_WIDTH, Math.round(width));
      }
    }
    if (Object.keys(widths).length > 0) parsed.widths = widths;
  }
  return Object.keys(parsed).length > 0 ? parsed : null;
}

/** Layer stored visibility over the table's defaults: default-hidden ids
 *  start false, the `shown` list flips its members true, then `hidden` flips
 *  its members false — an explicit hidden entry beats an explicit shown one
 *  (only possible via a corrupt payload). */
export function layerColumnVisibility(
  defaultHiddenIds: readonly string[],
  shown: readonly string[] | undefined,
  hidden: readonly string[] | undefined,
): VisibilityState {
  const vis: VisibilityState = {};
  for (const id of defaultHiddenIds) vis[id] = false;
  for (const id of shown ?? []) vis[id] = true;
  for (const id of hidden ?? []) vis[id] = false;
  return vis;
}

/** Split an editor's visibility map back into persisted `hidden`/`shown`
 *  lists: `hidden` carries only default-shown ids switched off, `shown` only
 *  default-hidden ids switched on. Absence in either list keeps meaning "the
 *  column's default", which is what lets a table grow a column without
 *  rewriting every stored payload. Both sorted so the same set always
 *  serializes identically.
 *
 *  `prevShown` counts as default-hidden alongside the roster because the
 *  roster can arrive late (workspace custom fields are a query): a commit
 *  made before it resolves would otherwise read every stored shown id as
 *  unknown and silently drop it. A shown id stays shown until the map
 *  explicitly hides it. */
export function splitColumnVisibility(
  visibility: VisibilityState,
  options: {
    defaultHiddenIds: readonly string[];
    prevShown: readonly string[];
  },
): { hidden: string[]; shown: string[] } {
  const defaultHidden = new Set([
    ...options.defaultHiddenIds,
    ...options.prevShown,
  ]);
  const hidden = Object.keys(visibility)
    .filter((id) => visibility[id] === false && !defaultHidden.has(id))
    .sort();
  const shown = Array.from(
    new Set([
      ...options.prevShown.filter((id) => visibility[id] !== false),
      ...Object.keys(visibility).filter(
        (id) => visibility[id] === true && defaultHidden.has(id),
      ),
    ]),
  ).sort();
  return { hidden, shown };
}

/** The controlled-customization props to spread onto `<DataTable>`. */
export type TableViewTableProps = {
  columnOrder: string[] | undefined;
  onColumnOrderChange: (order: string[]) => void;
  columnVisibility: VisibilityState;
  columnSizing: Record<string, number>;
  onColumnSizingChange: (sizing: Record<string, number>) => void;
};

/** Props to spread onto `<DataTableColumnSettings>` (add `columns`/`pinnedColumnIds`). */
export type TableViewSettingsPanelProps = {
  columnOrder: string[];
  columnVisibility: VisibilityState;
  onColumnOrderChange: (order: string[]) => void;
  onColumnVisibilityChange: (visibility: VisibilityState) => void;
  onReset: () => void;
};

type TableViewMirror = { view: TableViewState | null };

const MIRROR_DEFAULTS: TableViewMirror = { view: null };

const NO_DEFAULT_HIDDEN: string[] = [];

export function useTableViewSettings(
  tableId: string,
  options?: {
    /** Columns hidden until the user shows them (persisted in `shown` once
     *  they do). Pass a module-level constant — a fresh array per render
     *  would churn every derived memo. */
    defaultHiddenColumnIds?: string[];
  },
) {
  const defaultHiddenIds = options?.defaultHiddenColumnIds ?? NO_DEFAULT_HIDDEN;
  const { currentUser } = useAppData();
  const storageKey = `table-view:${tableId}`;

  const { state: mirror, update: updateMirror } =
    usePersistedState<TableViewMirror>(storageKey, MIRROR_DEFAULTS);
  const mirrorView = useMemo(
    () => parseTableViewState(mirror.view),
    [mirror.view],
  );

  const variables = useMemo(() => ({ key: storageKey }), [storageKey]);
  const { data } = useQuery(GetSettingDocument, {
    variables,
    fetchPolicy: "cache-and-network",
    skip: !currentUser,
  });
  // A server row may exist with an "empty" payload (reset stores `{}`), and
  // that is authoritative — but NO row at all must not beat a local mirror:
  // a mirror-only customization (e.g. a write lost to a closed tab) would
  // otherwise be discarded AND erased below.
  const serverHasRow = data?.setting != null;
  const serverView = useMemo(
    () => parseTableViewState(data?.setting ?? null),
    [data?.setting],
  );

  const [setSetting] = useMutation(SetSettingDocument);

  // Session edits — win until reload; null = no edit this session.
  const [local, setLocal] = useState<TableViewState | null>(null);

  const view = local ?? (serverHasRow ? serverView : mirrorView);
  const viewRef = useRef(view);
  viewRef.current = view;

  // Re-warm the mirror when the server disagrees (edited on another device)
  // so the NEXT load's first paint is already correct. Converges — once
  // equal, no further writes. Skipped while a session edit is pending so a
  // slow query can't clobber a newer local change, and skipped when the
  // server has no row at all so a mirror-only customization is never erased.
  useEffect(() => {
    if (!serverHasRow || local !== null) return;
    if (JSON.stringify(serverView) !== JSON.stringify(mirror.view ?? null)) {
      updateMirror({ view: serverView });
    }
  }, [serverHasRow, serverView, mirror.view, local, updateMirror]);

  // Server write. The cache update mirrors use-discovery: the setting query
  // has no normalized entity, so write it explicitly to keep a later cache
  // read consistent with what was saved.
  const writeToServer = useCallback(
    (value: TableViewState) => {
      const cacheValue = Object.keys(value).length > 0 ? value : null;
      setSetting({
        variables: {
          key: storageKey,
          value: value as unknown as Record<string, never>,
        },
        optimisticResponse: { setSetting: true },
        update: (cache) => {
          cache.writeQuery({
            query: GetSettingDocument,
            variables: { key: storageKey },
            data: {
              // `Exclude<…, undefined>`: the write always SETS the key, so the
              // cached query is a real answer rather than an absent field.
              setting: cacheValue as Exclude<SettingValue, undefined>,
            },
          });
        },
        onError: (err) => toastMutationError(err, "Failed to save table view"),
      });
    },
    [setSetting, storageKey],
  );

  // Debounced so a burst of toggles lands as one mutation. The refs track
  // what is pending vs what actually reached the server (reference equality
  // is enough — commit/debounce pass the same object through).
  const [pendingWrite, setPendingWrite] = useState<TableViewState | null>(
    null,
  );
  const pendingWriteRef = useRef<TableViewState | null>(null);
  const sentWriteRef = useRef<TableViewState | null>(null);
  const debouncedWrite = useDebouncedValue(pendingWrite, PERSIST_DEBOUNCE_MS);
  useEffect(() => {
    if (debouncedWrite === null) return;
    sentWriteRef.current = debouncedWrite;
    writeToServer(debouncedWrite);
  }, [debouncedWrite, writeToServer]);

  // Flush a write still inside the debounce window when the hook unmounts or
  // the table id changes — the debounce timer is cancelled on unmount, so
  // without this an edit made <600ms before navigating away (or closing a
  // dialog) reaches the localStorage mirror but never the server, silently
  // diverging this device from every other session.
  useEffect(() => {
    return () => {
      const pending = pendingWriteRef.current;
      if (pending !== null && pending !== sentWriteRef.current) {
        sentWriteRef.current = pending;
        writeToServer(pending);
      }
    };
  }, [writeToServer]);

  const commit = useCallback(
    (next: TableViewState) => {
      // Eager: a single gesture can commit twice in one tick (showing a
      // column writes the order AND the visibility), and the handlers read
      // `viewRef` — a render-only ref would hand the second commit a stale
      // view that silently reverts the first.
      viewRef.current = next;
      setLocal(next);
      updateMirror({ view: parseTableViewState(next) });
      pendingWriteRef.current = next;
      setPendingWrite(next);
    },
    [updateMirror],
  );

  const onColumnOrderChange = useCallback(
    (order: string[]) => {
      commit({ ...(viewRef.current ?? {}), order });
    },
    [commit],
  );

  const onColumnVisibilityChange = useCallback(
    (visibility: VisibilityState) => {
      const prev = viewRef.current ?? {};
      const { hidden, shown } = splitColumnVisibility(visibility, {
        defaultHiddenIds,
        prevShown: prev.shown ?? [],
      });
      const next: TableViewState = { ...prev };
      if (hidden.length > 0) next.hidden = hidden;
      else delete next.hidden;
      if (shown.length > 0) next.shown = shown;
      else delete next.shown;
      commit(next);
    },
    [commit, defaultHiddenIds],
  );

  const onColumnSizingChange = useCallback(
    (sizing: Record<string, number>) => {
      const next: TableViewState = { ...(viewRef.current ?? {}) };
      if (Object.keys(sizing).length > 0) next.widths = sizing;
      else delete next.widths;
      commit(next);
    },
    [commit],
  );

  const onReset = useCallback(() => {
    commit({});
  }, [commit]);

  const columnOrder = view?.order;
  const columnVisibility = useMemo<VisibilityState>(() => {
    const vis = layerColumnVisibility(defaultHiddenIds, view?.shown, view?.hidden);
    return Object.keys(vis).length > 0 ? vis : EMPTY_VISIBILITY;
  }, [defaultHiddenIds, view?.shown, view?.hidden]);
  const columnSizing = view?.widths ?? EMPTY_SIZING;

  return useMemo<{
    tableProps: TableViewTableProps;
    settingsProps: TableViewSettingsPanelProps;
  }>(
    () => ({
      tableProps: {
        columnOrder,
        onColumnOrderChange,
        columnVisibility,
        columnSizing,
        onColumnSizingChange,
      },
      settingsProps: {
        columnOrder: columnOrder ?? [],
        columnVisibility,
        onColumnOrderChange,
        onColumnVisibilityChange,
        onReset,
      },
    }),
    [
      columnOrder,
      columnVisibility,
      columnSizing,
      onColumnOrderChange,
      onColumnVisibilityChange,
      onColumnSizingChange,
      onReset,
    ],
  );
}
